
  import { defineComponent, PropType } from 'vue';
  import { mapActions, mapGetters } from 'vuex';

  import ComponentInput from '@/common/components/ComponentInput.vue';
  import InputBlock from '@/common/components/redesigned/InputBlock.vue';
  import TreeMultiselect from '@/common/components/redesigned/TreeMultiselect/TreeMultiselect.vue';
  import { MenuModes } from '@/common/components/redesigned/TreeMultiselect/types';

  import { REGIONS_RF_LEVEL_EVENTS_LABELS } from '../../../constants/tree';
  import { FTS_WEEKLY_ACTIONS_NAME, FTS_WEEKLY_GETTERS_NAME } from '../../../store/ftsWeekly/name';
  import { VED_COMMON_ACTIONS_NAME, VED_COMMON_GETTERS_NAME } from '../../../store/vedCommon/name';
  import { TFTSExporter, TFTSWeeklyMovementOfGoodsFilter } from '../../../types/fts-weekly';
  import {
    pruneWeeklyMovementCustomsIds,
    pruneWeeklyMovementTransportIds,
  } from '../../../utils/fts/userGroupFilterPrune';
  import UserCustomsHouseListsModal from '../../user-group-lists-modal/UserCustomsHouseListsModal.vue';
  import UserTransportListsModal from '../../user-group-lists-modal/UserTransportListsModal.vue';
  import FTSFilterWrapperGroup from '../fts-filter/FTSFilterWrapperGroup.vue';
  import FTSFilterWrapperRow from '../fts-filter/FTSFilterWrapperRow.vue';
  import FTSCustomProcedureField from '../FTSCustomProcedureField.vue';

  export default defineComponent({
    name: 'FTSWeeklyMovementOfGoodsFilter',
    components: {
      FTSCustomProcedureField,
      ComponentInput,
      TreeMultiselect,
      InputBlock,
      FTSFilterWrapperRow,
      FTSFilterWrapperGroup,
      UserCustomsHouseListsModal,
      UserTransportListsModal,
    },
    props: {
      modelValue: {
        type: Object as PropType<TFTSWeeklyMovementOfGoodsFilter>,
        required: true,
      },
      disabled: {
        type: Boolean,
        default: false,
      },
    },
    emits: ['update:modelValue'],
    data(): {
      localFilter: TFTSWeeklyMovementOfGoodsFilter;
      isCustomsModalOpen: boolean;
      isTransportModalOpen: boolean;
    } {
      return {
        localFilter: this.getMovementOfGoodsFilter(this.modelValue),
        isCustomsModalOpen: false,
        isTransportModalOpen: false,
      };
    },
    computed: {
      ...mapGetters({
        getCustomsOrgansOnBorderTree: VED_COMMON_GETTERS_NAME.getCustomsOrgansOnBorderTree,
        getDeliveryTerms: FTS_WEEKLY_GETTERS_NAME.getDeliveryTerms,
        getTransportTypes: FTS_WEEKLY_GETTERS_NAME.getTransportTypes,
        getDeliveryGoodsPoints: FTS_WEEKLY_GETTERS_NAME.getDeliveryGoodsPoints,
        getExporters: FTS_WEEKLY_GETTERS_NAME.getExporters,
        getRegionsRfTree: VED_COMMON_GETTERS_NAME.getRegionsRfTree,
      }),
      getRegionsRfLevelEventsLabels() {
        return REGIONS_RF_LEVEL_EVENTS_LABELS;
      },
      menuModes(): typeof MenuModes {
        return MenuModes;
      },
    },
    created() {
      this.searchCustomsOrgansOnBorderTree();
      this.searchDeliveryTerms();
      this.searchTransportTypes();
      this.searchDeliveryGoodsPoints();
      this.searchExporters();
      this.searchRegionsRfTree();
    },
    methods: {
      ...mapActions({
        searchCustomsOrgansOnBorderTree: VED_COMMON_ACTIONS_NAME.searchCustomsOrgansOnBorderTree,
        searchDeliveryTerms: FTS_WEEKLY_ACTIONS_NAME.searchDeliveryTerms,
        searchTransportTypes: FTS_WEEKLY_ACTIONS_NAME.searchTransportTypes,
        searchDeliveryGoodsPoints: FTS_WEEKLY_ACTIONS_NAME.searchDeliveryGoodsPoints,
        searchExporters: FTS_WEEKLY_ACTIONS_NAME.searchExporters,
        searchRegionsRfTree: VED_COMMON_ACTIONS_NAME.searchRegionsRfTree,
      }),
      getMovementOfGoodsFilter(filter: TFTSWeeklyMovementOfGoodsFilter): TFTSWeeklyMovementOfGoodsFilter {
        return {
          customsProcedureIds: [...filter.customsProcedureIds],
          deliveryTermsIds: [...filter.deliveryTermsIds],
          transportTypesOnBorderIds: [...filter.transportTypesOnBorderIds],
          transportTypesInsideCountryIds: [...filter.transportTypesInsideCountryIds],
          customsOrgansOnBorderIds: [...filter.customsOrgansOnBorderIds],
          regionIds: [...filter.regionIds],
          deliveryGoodsPointIds: [...filter.deliveryGoodsPointIds],
          exporterIds: [...filter.exporterIds],
          isProducer: filter.isProducer,
          isContainer: filter.isContainer,
          isCurrency: filter.isCurrency,
        };
      },
      handleOpenCustomsModal() {
        this.isCustomsModalOpen = true;
      },
      handleCloseCustomsModal() {
        this.isCustomsModalOpen = false;
      },
      handleOpenTransportModal() {
        this.isTransportModalOpen = true;
      },
      handleCloseTransportModal() {
        this.isTransportModalOpen = false;
      },
      handleDeletedCustomsSubtree(ids: number[]) {
        this.localFilter = pruneWeeklyMovementCustomsIds(this.localFilter, ids);
      },
      handleDeletedTransportSubtree(ids: number[]) {
        this.localFilter = pruneWeeklyMovementTransportIds(this.localFilter, ids);
      },
      getExporterLabel(item: TFTSExporter) {
        return `${item.inn} ${item.name}`;
      },
    },
    watch: {
      modelValue: {
        handler(newValue: TFTSWeeklyMovementOfGoodsFilter) {
          const newMovementOfGoodsFilter = this.getMovementOfGoodsFilter(newValue);
          if (JSON.stringify(this.localFilter) !== JSON.stringify(newMovementOfGoodsFilter)) {
            this.localFilter = newMovementOfGoodsFilter;
          }
        },
      },
      localFilter: {
        handler(newValue: TFTSWeeklyMovementOfGoodsFilter) {
          this.$emit('update:modelValue', { ...this.modelValue, ...newValue });
        },
        deep: true,
      },
    },
  });
